import {Injectable} from '@angular/core';
import {DirectusApiService} from '@app/providers/expo/directus/directus-api.service';
import {
  IDirectusExpoOrderList,
  IDirectusExpoOrder,
} from '@app/models/expo-order.interface';
import {BehaviorSubject, iif, Observable, of, throwError} from 'rxjs';
import {catchError, map, mergeMap, retry, switchMap, tap} from 'rxjs/operators';
import {DirectusUserService} from '@app/providers/expo/directus/directus-user.service';
import {Select, Store} from '@ngxs/store';
import moment from 'moment';
import {ModeService} from '@app/providers/mode.service';
import {OloPayTerminalService} from '@app/services/olo-pay/olo-pay-terminal.service';
import {SetRole} from '@app/store/actions/user.action';
import {UserStateModel} from '@app/store/state/user.state';
import {Order} from '../../../../assets/chepri-modules/src/models/olo.order';
import {
  DirectusSchema,
  ExpoMetadata,
  Locations,
  ErrorLog,
} from '@app/providers/expo/directus/directus-collections.interface';
// (imports consolidated above)

@Injectable({
  providedIn: 'root',
})
export class DirectusExpoProvider {
  roleID = '4'; // ID for the 'Location Role'
  errorSubject = new BehaviorSubject<string>(null);
  error$: Observable<string> = this.errorSubject.asObservable();
  private currentLocationKey = 'currentLocation';
  private currentLocationDirectusID = 'locationDirectusID';
  private currentLocation: string;

  constructor(
    private directusAPI: DirectusApiService,
    private user: DirectusUserService,
    private oloPayTerminal: OloPayTerminalService,
    private store: Store,
    private mode: ModeService
  ) {}

  getClient() {
    return this.directusAPI.client;
  }

  // loginExpoUser(form: any): Promise<{ status: string; data: any }> {
  loginExpoUser(form: any) {
    this.errorSubject.next('');
    return this.user
      .loginUser(form.emailAddress, form.password, form.remember)
      .pipe(
        switchMap((currentUser) => {
          return this.user.getMe().pipe(
            map((user) => {
              this.setCurrentID(user.id);
              const isMaster =
                user.role === '07fbb946-5744-44d6-b805-302fd48b261f';
              this.store.dispatch(new SetRole(user));
              this.setCurrentLocation(
                isMaster ? 'master' : user.location_restaurant_id
              );
              // OloPay terminals don't require complex initialization like trancloud did
              // Terminal configuration is handled when needed by the OloPayTerminalService
              this.getCurrentLocation();
              return user;
            })
          );
        })
      );
  }

  logoutExpoUser() {
    this.clearStoredLocation();
    this.clearStoredDirectusID();
    clearInterval(this.user.refreshInterval);
    return this.user.logoutUser();
  }

  getRoles() {
    return this.user.getRoles();
  }

  getExpoOrders() {
    return this.directusAPI.getItems('expo_metadata');
  }

  getExpoPastOrders(date: string, location: string) {
    const dateMoment = moment(date, 'YYYY-MM-DD');
    const queryObject = {
      filter: {
        'year(ready_time)': {_eq: dateMoment.year()},
        'month(ready_time)': {_eq: dateMoment.format('M')},
        'day(ready_time)': {_eq: dateMoment.date()},
        location: {_eq: location},
        is_kiosk: {_eq: true},
      },
      sort: '-created_on',
      limit: -1,
    };
    return this.directusAPI.getQueriedItems<ExpoMetadata>(
      'expo_metadata',
      queryObject
    );
  }

  getQueuedExpoOrders(date: string, location: string) {
    const dateMoment = moment(date, 'YYYY-MM-DD');
    const queryObject = {
      filter: {
        order_status: {_nin: ['fired', 'canceled', 'refunded']},
        'year(ready_time)': {_eq: dateMoment.year()},
        'month(ready_time)': {_eq: dateMoment.format('M')},
        'day(ready_time)': {_eq: dateMoment.date()},
        location: {_eq: location},
        order_type: {_eq: 'dinein'},
      },
      sort: 'ready_time',
      limit: -1,
    };
    return this.directusAPI.getQueriedItems<ExpoMetadata>(
      'expo_metadata',
      queryObject
    );
  }

  getFiredExpoOrders(date: string, location: string) {
    const dateMoment = moment(date, 'YYYY-MM-DD');
    const queryObject = {
      filter: {
        order_status: {_in: ['fired', 'refunded']},
        'year(ready_time)': {_eq: dateMoment.year()},
        'month(ready_time)': {_eq: dateMoment.format('M')},
        'day(ready_time)': {_eq: dateMoment.date()},
        order_type: {_eq: 'dinein'},
        location: {_eq: location},
      },
      sort: '-fired_time',
      limit: -1,
    };
    return this.directusAPI.getQueriedItems<ExpoMetadata>(
      'expo_metadata',
      queryObject
    );
  }

  postExpoOrder(
    pb: Order,
    user: UserStateModel | null | undefined,
    manualFireTime: any,
    tableNumber: string,
    invoiceNumber: string,
    cardNumber: string,
    cardType: string,
    authCode: string,
    refNo: string,
    recordNo: string,
    cashGiven?: number,
    changeAmount?: number
  ): Observable<any> {
    manualFireTime = manualFireTime ? manualFireTime.replace(' ', 'T') : null;
    const body: Partial<ExpoMetadata> = this.convertExpoOrder(
      pb,
      user,
      manualFireTime,
      invoiceNumber,
      tableNumber,
      cardNumber,
      cardType,
      authCode,
      refNo,
      recordNo,
      cashGiven,
      changeAmount
    );
    const queryObject = {
      filter: {
        olo_id: {_eq: body.location},
      },
    };
    return this.directusAPI
      .getQueriedItems<Locations>('locations', queryObject)
      .pipe(
        switchMap((res) => {
          body.location = res[0].id;
          return this.directusAPI.postItem('expo_metadata', body);
        })
      );
  }

  updateExpoOrder(body: Partial<ExpoMetadata>): Observable<ExpoMetadata> {
    return this.getExpoOrder(body.id).pipe(
      switchMap((res) => {
        const updateBody: Partial<ExpoMetadata> = {};
        (Object.keys(body) as (keyof ExpoMetadata)[]).forEach((key) => {
          const newValue = body[key];
          if (newValue !== undefined && newValue !== res[key]) {
            (updateBody as any)[key] = newValue;
          }
        });
        return this.directusAPI.patchItem<ExpoMetadata>(
          body.id,
          'expo_metadata',
          updateBody
        );
      })
    );
  }

  logError(
    errorType: 'capacity_service' | 'expo_service',
    errorMessage: string,
    orderDetails?: {
      oloId?: string;
      basketId?: string;
      restaurantId?: number;
    }
  ): Observable<any> {
    const details = {
      error_type: errorType,
      error_message: errorMessage,
      olo_order_id: orderDetails?.oloId || null,
      basket_id: orderDetails?.basketId || null,
      restaurant_id: orderDetails?.restaurantId || null,
      timestamp: new Date().toISOString(),
      environment: window.location.hostname.includes('localhost')
        ? 'development'
        : 'production',
    };

    const body: Partial<ErrorLog> = {
      error: `${errorType}: ${errorMessage}`,
      response: JSON.stringify(details),
      location: orderDetails?.restaurantId,
    };

    return this.directusAPI.postItem<ErrorLog>('error_log', body).pipe(
      tap(() => console.log('Error logged to Directus:', body)),
      // Never let error logging block the main flow
      catchError((logError) => {
        console.error('Failed to log error to Directus:', logError);
        return of(null);
      })
    );
  }

  getExpoOrder(id: number) {
    return this.directusAPI.getSingleItem<ExpoMetadata>('expo_metadata', id);
  }

  // will return an observable containing an array of one item, since order ids are unique
  getExpoOrderByOrderId(
    order_id: string,
    fallback_id?: string
  ): Observable<ExpoMetadata[]> {
    const filters: any[] = [{order_id: {_eq: order_id}}];
    if (fallback_id) {
      filters.push({order_id: {_eq: fallback_id}});
    }

    const queryObject = {
      filter: {
        _or: filters,
      },
    };

    return this.directusAPI
      .getQueriedItems<ExpoMetadata>('expo_metadata', queryObject)
      .pipe(
        map((res) => {
          if (!res || res.length === 0) {
            throw new Error('Order not found');
          }
          return res;
        }),
        retry({count: 15, delay: 2000}),
        catchError(() => of([]))
      );
  }

  deleteExpoOrder(body: IDirectusExpoOrder): Observable<any> {
    return this.directusAPI.deleteItem(body.id, 'expo_metadata');
  }

  getKioskCoupons() {
    return this.directusAPI.getItems('kiosk_coupons');
  }

  // being an API call, if location is master this will return the list of all locations
  // this serves a dual purpose, one is the return value, the other is to call getCurrentLocation()
  getLocationsByUserRef() {
    const locationName = this.getCurrentLocation();
    let queryObject = {};

    if (locationName !== 'master') {
      queryObject = {
        filter: {
          id: {_eq: locationName},
        },
      };
    }

    return this.directusAPI.getQueriedItems<Locations>(
      'locations',
      queryObject
    );
  }

  getCurrentLocation(): string {
    if (this.currentLocation === undefined) {
      this.currentLocation = this.getStoredLocation();
    }
    return this.currentLocation;
  }

  setCurrentLocation(newLocation: string): void {
    this.currentLocation = newLocation;
    this.updateStoredLocation(newLocation);
  }

  setCurrentID(id: string): void {
    this.updateStoredDirectusID(id);
  }

  convertExpoOrder(
    pb: Order,
    user: UserStateModel | null | undefined,
    manualFireTime: any,
    invoiceNumber: string,
    tableNumber: string,
    cardNumber: string,
    cardType: string,
    authCode: string,
    refNo: string,
    recordNo: string,
    cashGiven?: number,
    changeAmount?: number
  ): Partial<ExpoMetadata> {
    let order: Partial<ExpoMetadata>;
    const time = this.readyTimeToDate(pb.readytime);
    const isKiosk =
      this.mode.getStoredMode() === 'kms' ||
      this.mode.getStoredMode() === 'cks';
    order = {
      check_in_time: null,
      customer_name: this.resolveCustomerName(user),
      fired_time: null,
      location: pb.vendorid,
      order_id: pb.id,
      order_reference: pb.orderref,
      order_status: !tableNumber ? 'placed' : 'pre-fired',
      order_type: pb.deliverymode,
      party_size: user?.partySize?.toString(),
      ready_time:
        manualFireTime && pb.deliverymode === 'dinein'
          ? manualFireTime
          : moment(time).format('YYYY-MM-DDTHH:mm:ss'),
      table_number: tableNumber,
      table_preference: 'any',
      is_kiosk: isKiosk,
      invoice_number: invoiceNumber, // cardNumber, cardType, authCode, refNo
      is_refunded: null,
      card_number: cardNumber,
      card_type: cardType,
      auth_code: authCode,
      ref_number: refNo,
      record_number: recordNo,
      refund_auth_code: null,
      refund_card_number: null,
      refund_card_type: null,
      refund_ref_number: null,
      cash_given: cashGiven,
      change_amount: changeAmount,
    };
    return order;
  }

  // dineInName is only populated by dine-in flows and can be wiped by state
  // resets; this runs after the Olo order has already been charged, so a
  // missing name must never throw. `user` arrives unguarded -- basket.state.ts
  // hands the store snapshot straight to postExpoOrder on the post-charge path.
  // That it can be absent is not speculative: the same file null-checks the
  // same value (`if (!!user && ...)`) when reading authtoken, and that block
  // does not gate the handoff. So nullability is absorbed here, downstream,
  // rather than at the call site -- hence the optional chaining below and the
  // nullable parameter all the way up from postExpoOrder.
  private resolveCustomerName(user: UserStateModel | null | undefined): string {
    const dineInName = [user?.dineInName?.firstname, user?.dineInName?.lastname]
      .filter(Boolean)
      .join(' ');
    if (dineInName) {
      return dineInName;
    }
    return [user?.info?.firstname, user?.info?.lastname]
      .filter(Boolean)
      .join(' ');
  }

  readyTimeToDate(time: string): Date {
    return new Date(
      Number(time.substring(0, 4)),
      Number(time.substring(4, 6)) - 1,
      Number(time.substring(6, 8)),
      Number(time.substring(9, 11)),
      Number(time.substring(12))
    );
  }

  updateStoredLocation(newLocation: string) {
    localStorage.setItem(this.currentLocationKey, newLocation);
  }

  getStoredLocation(): string {
    return localStorage.getItem(this.currentLocationKey);
  }

  clearStoredLocation() {
    localStorage.removeItem(this.currentLocationKey);
  }

  updateStoredDirectusID(newID: string) {
    localStorage.setItem(this.currentLocationDirectusID, newID);
  }

  getStoredDirectusID(): string {
    return localStorage.getItem(this.currentLocationDirectusID);
  }

  clearStoredDirectusID() {
    localStorage.removeItem(this.currentLocationDirectusID);
  }
}
